import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetIcon,
  ActionsheetItem,
  ActionsheetItemText,
} from "@/components/ui/actionsheet";
import {
  ParticipantDTO,
  ParticipantDTORole,
  WalletDTO,
} from "@/generated/api/models";
import { InterfaceActionsheetProps } from "@gluestack-ui/actionsheet/lib/types";
import {
  DoorOpen,
  Edit,
  Heading,
  QrCode,
  Send,
  Trash,
} from "lucide-react-native";
import { useState } from "react";
import { useAccessTokenClaims, useSession } from "@/utils/session.ctx";
import {
  RestaurantTeamModalDelete,
  RestaurantTeamModalEdit,
  RestaurantTeamModalQRCode,
} from "./restaurant.team.modal";
import { Card } from "@/components/ui/card";
import { HStack } from "@/components/ui/hstack";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import { getName } from "@/utils/name";
import { VStack } from "@/components/ui/vstack";
import { RestaurantTeamItemMini } from "./restaurant.team.item";

export function RestaurantTeamActionSheet({
  organizationId,
  participant,
  onClose,
  role,
  ...rest
}: InterfaceActionsheetProps & {
  organizationId: string;
  participant: ParticipantDTO | undefined;
  role: ParticipantDTORole | undefined;
}) {
  const [editIsOpened, setEditIsOpened] = useState(false);
  const [deleteIsOpened, setDeleteIsOpened] = useState(false);
  const [qrIsOpened, setQrIsOpened] = useState(false);

  const claims = useAccessTokenClaims();

  const userId = claims?.externalClaims?.userId;

  return (
    <>
      {participant && (
        <>
          <RestaurantTeamModalQRCode
            organizationId={organizationId}
            participant={participant}
            isOpen={qrIsOpened}
            onClose={() => setQrIsOpened(false)}
          />
          <RestaurantTeamModalEdit
            organizationId={organizationId}
            participant={participant}
            isOpen={editIsOpened}
            onClose={() => setEditIsOpened(false)}
          />
          <RestaurantTeamModalDelete
            organizationId={organizationId}
            participant={participant}
            isOpen={deleteIsOpened}
            onClose={() => setDeleteIsOpened(false)}
          />
        </>
      )}
      <Actionsheet closeOnOverlayClick onClose={onClose} {...rest}>
        <ActionsheetBackdrop />
        <ActionsheetContent>
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>
          {participant && <RestaurantTeamItemMini participant={participant} variant="short" />}
          <ActionsheetItem
            onPress={() => {
              onClose?.();
              setQrIsOpened(true);
            }}
          >
            <ActionsheetIcon
              className="stroke-background-700"
              as={QrCode}
              size={16}
            />
            <ActionsheetItemText>Get QR-code</ActionsheetItemText>
          </ActionsheetItem>
          {role !== "USER" && (
            <ActionsheetItem
              onPress={() => {
                onClose?.();
                setEditIsOpened(true);
              }}
            >
              <ActionsheetIcon className="stroke-background-700" as={Edit} />
              <ActionsheetItemText>Manage tags and role</ActionsheetItemText>
            </ActionsheetItem>
          )}

          {(role !== "USER" || userId === participant?.userId) && (
            <ActionsheetItem
              onPress={() => {
                onClose?.();
                setDeleteIsOpened(true);
              }}
              isDisabled={
                participant?.role === "OWNER" ||
                (participant?.role === "ADMIN" &&
                  role !== "OWNER" &&
                  userId !== participant?.userId)
              }
            >
              <ActionsheetIcon
                className="stroke-background-700"
                as={DoorOpen}
              />
              <ActionsheetItemText className="text-red-500">
                {userId === participant?.userId ? "Leave" : "Remove"}
              </ActionsheetItemText>
            </ActionsheetItem>
          )}
        </ActionsheetContent>
      </Actionsheet>
    </>
  );
}
